import { fetchGQL } from '../common'

const PROPERTIES_LIMIT = process.env.PROPERTIES_LIMIT || 100
const RECENT_PROPERTIES_LIMIT = process.env.RECENT_PROPERTIES_LIMIT || 8
const SIMILAR_PROPERTIES_LIMIT = process.env.SIMILAR_PROPERTIES_LIMIT || 3
const SAVED_SEARCHES_LIMIT = 100

const propertyFields = [
  'id',
  'name',
  'address',
  'description',
  'city',
  'state',
  'zip',
  'price',
  'lat',
  'lon',
  'sqft',
  'beds',
  'baths',
  'year',
  'showZestimate',
  'createdAt',
  'amenities',
  'arv',
  'repairEstimate',
  'type',
  'homeType',
  'images',
  'defaultImage',
  'startListingOn',
  'sellerLeadStage',
].join()

const propertyFilterFields = ['name', 'type', 'value'].join()

const franchiseeBasicFields = ['id', 'name', 'phone', 'email', 'subdomain', 'isActive'].join()

const allFranchiseeFields = ['profileImageUrl', 'bannerImageUrl', 'description', 'address', 'city', 'state', 'zip', 'country', 'lat', 'lon']
  .concat(franchiseeBasicFields)
  .join()

const adCouncilFields = ['id', 'name', 'subdomain', 'description', 'location', 'profileImageUrl', 'bannerImageUrl', 'phone'].join()

const userFields = [
  'id',
  'firstName',
  'lastName',
  'email',
  'phone',
  'locations',
  'zip',
  'propertyIds',
  'avatarUrl',
  'emailNotifications',
  'isPremium',
  'stripeLast4',
  'loginCount',
  'stripeBrand',
].join()

const notificationFields = ['id', 'message', 'displayAt', 'isRead'].join()

export async function fetchPropertyFilters() {
  const {
    data: { propertyFilters },
  } = await fetchGQL({ query: `query { propertyFilters { ${propertyFilterFields} } }` })
  return propertyFilters
}

export async function fetchRecentProperties({ lat, lon }, limit = RECENT_PROPERTIES_LIMIT) {
  const {
    data: { properties, propertyCount },
  } = await fetchGQL({
    query: `query { propertyCount, properties(geoPoint: { lat: ${lat}, lon: ${lon} }, limit: ${limit}) { ${propertyFields} } }`,
  })

  return { properties, propertyCount }
}

export async function fetchSimilarProperties(id, limit = SIMILAR_PROPERTIES_LIMIT) {
  const {
    data: { similarProperties },
  } = await fetchGQL({ query: `query { similarProperties(id: "${id}", limit: ${limit}) { ${propertyFields} } }` })

  return similarProperties
}

export async function fetchPropertiesInGeoRect(geoRect, filters = {}, limit = PROPERTIES_LIMIT, offset = 0) {
  const query = `query($geoRect: GeoRectangleInput, $filters: PropertyFilterInput) {
    propertyCount(geoRect: $geoRect, filters: $filters),
    properties(geoRect: $geoRect, filters: $filters, limit: ${limit}, offset: ${offset}) { ${propertyFields} }
  }`
  const variables = { geoRect, filters }

  const {
    data: { properties, propertyCount },
  } = await fetchGQL({ query, variables })

  return { properties, propertyCount }
}

export async function fetchFranchisee(id) {
  const {
    data: { franchisee },
  } = await fetchGQL({ query: `query { franchisee(id: ${id}) { ${allFranchiseeFields}, properties { ${propertyFields} } } }` })

  return franchisee
}

export async function fetchAdCouncil(id) {
  const {
    data: { adCouncil },
  } = await fetchGQL({ query: `query { adCouncil(id: ${id}) { ${adCouncilFields} } }` })

  return adCouncil
}

export async function fetchProperty(id, token) {
  const { data, errors } = await fetchGQL({ query: `query { property(id: "${id}") { ${propertyFields}, franchisee { ${franchiseeBasicFields} } } }` }, token)

  return { property: data ? data.property : null, errors }
}

export async function createUser({ firstName, lastName, email, password, phone, location, zip, facebookToken, googleToken, stripeToken }) {
  const {
    data: {
      createUser: { user, token, errors },
    },
  } = await fetchGQL({
    query: `mutation($firstName: String, $lastName: String, $email: String, $password: String, $phone: String, $location: [String!], $zip: String, $facebookToken: String, $googleToken: String, $stripeToken: String) { createUser(
      firstName: $firstName,
      lastName: $lastName,
      email: $email,
      password: $password,
      phone: $phone,
      locations: $location,
      zip: $zip,
      facebookToken: $facebookToken,
      googleToken: $googleToken,
      stripeToken: $stripeToken
    ) { user { ${userFields.concat(', stripeToken')} }, errors, token }}`,
    variables: { firstName, lastName, email, password, phone, location, zip, facebookToken, googleToken, stripeToken },
  })

  return { user, token, errors }
}

export async function authenticate({ email, password, facebookToken, googleToken }) {
  const {
    data: {
      authenticate: { user, token, errors },
    },
  } = await fetchGQL({
    query: `mutation($email: String, $password: String, $facebookToken: String, $googleToken: String) { authenticate(
      email: $email,
      password: $password,
      facebookToken: $facebookToken,
      googleToken: $googleToken
    ) { user { ${userFields} }, token, errors }}`,
    variables: { email, password, facebookToken, googleToken },
  })
  return { user, token, errors }
}

export async function addPropertyToFavorites(propertyId, token) {
  const {
    data: {
      addPropertyToFavorites: { errors },
    },
  } = await fetchGQL(
    {
      query: `mutation { addPropertyToFavorites(id: ${propertyId}) { errors } }`,
      variables: { propertyId },
    },
    token
  )

  return { errors }
}

export async function removePropertyFromFavorites(propertyId, token) {
  const {
    data: {
      removePropertyFromFavorites: { errors },
    },
  } = await fetchGQL(
    {
      query: `mutation { removePropertyFromFavorites(id: ${propertyId}) { errors } }`,
      variables: { propertyId },
    },
    token
  )

  return { errors }
}

export async function savedProperties(token) {
  const {
    data: {
      me: { savedProperties },
    },
  } = await fetchGQL({ query: `query { me { savedProperties { ${propertyFields}  } } }` }, token)

  return { savedProperties }
}

export async function updatePassword({ password, resetPasswordToken }) {
  const {
    data: {
      updatePassword: { errors },
    },
  } = await fetchGQL({
    query: `mutation($password: String!, $resetPasswordToken: String!) {
      updatePassword(password: $password, resetPasswordToken: $resetPasswordToken) { errors } }`,
    variables: { password, resetPasswordToken },
  })

  return { errors }
}

export async function resetPassword(email) {
  const {
    data: {
      resetPassword: { errors },
    },
  } = await fetchGQL({
    query: `mutation($email: String!) { resetPassword(email: $email) { errors } }`,
    variables: { email },
  })

  return { errors }
}

export async function resendActivationEmail(email) {
  const {
    data: {
      resendActivationEmail: { errors },
    },
  } = await fetchGQL({
    query: `mutation($email: String!) { resendActivationEmail(email: $email) { errors } }`,
    variables: { email },
  })

  return { errors }
}

export async function userInfo(token) {
  const {
    data: { me: user },
  } = await fetchGQL({ query: `query { me { ${userFields} } }` }, token)

  return { user }
}

export async function saveSearch(token, geoRect, filters = {}, location) {
  const {
    data: {
      saveSearch: { errors },
    },
  } = await fetchGQL(
    {
      query: `mutation saveSearch($geoRect: GeoRectangleInput!, $filters: PropertyFilterInput!, $location: String!) {
      saveSearch(filters: $filters, geoRect: $geoRect, location: $location)  { errors } }`,
      variables: { geoRect, filters, location },
    },
    token
  )

  return { errors }
}

export async function removeNotification(token, notificationId) {
  const {
    data: {
      removeNotification: { errors },
    },
  } = await fetchGQL(
    {
      query: `mutation { removeNotification(id: ${notificationId}) { errors } }`,
    },
    token
  )

  return { errors }
}

export async function removeSearch(token, savedSeachId) {
  const {
    data: {
      removeSearch: { errors },
    },
  } = await fetchGQL(
    {
      query: `mutation { removeSearch(id: ${savedSeachId}) { errors } }`,
    },
    token
  )

  return { errors }
}

export async function fetchSavedSearches(token, limit = SAVED_SEARCHES_LIMIT) {
  const {
    data: {
      me: { savedSearches },
    },
  } = await fetchGQL(
    { query: `query { me { savedSearches(limit: ${limit}) { id location filters {beds baths types homeTypes areaMin areaMax priceMin priceMax} } } }` },
    token
  )

  return { savedSearches }
}

export async function fetchNotifications(token) {
  const {
    data: {
      me: { notifications },
    },
  } = await fetchGQL({ query: `query { me { notifications { ${notificationFields} , property { ${propertyFields} } } } }` }, token)

  return { notifications }
}

export async function updateUser(params, token) {
  const {
    data: {
      updateProfile: { errors },
    },
  } = await fetchGQL(
    {
      query: `mutation updateProfile( $params: UpdateProfileInput!) {updateProfile(params: $params) { errors }}`,
      variables: { params },
    },
    token
  )

  return { errors }
}

export async function markNotificationRead(token) {
  const {
    data: {
      markNotificationsAsRead: { errors },
    },
  } = await fetchGQL(
    {
      query: `mutation { markNotificationsAsRead { errors } }`,
    },
    token
  )

  return { errors }
}

export async function requestInfo(data, token) {
  const { firstName, lastName, email, phone, propertyId, message } = data

  const {
    data: {
      requestInfo: { errors },
    },
  } = await fetchGQL(
    {
      query: `mutation { requestInfo(
        firstName: "${firstName}",
        lastName: "${lastName}",
        email: "${email}",
        phone: "${phone}",
        propertyId: ${propertyId},
        message: "${message}"
      ) { errors }}`,
    },
    token
  )
  return { errors }
}

export async function updateUserAvatar(base64DataString, token) {
  const {
    data: {
      setAvatar: { errors, avatarUrl },
    },
  } = await fetchGQL(
    {
      query: `mutation setAvatar( $data: String) {setAvatar(data: $data) { errors, avatarUrl }}`,
      variables: { data: base64DataString },
    },
    token
  )
  return { errors, avatarUrl }
}

export async function createSubscription(stripeToken, token) {
  const {
    data: {
      createSubscription: { errors },
    },
  } = await fetchGQL(
    {
      query: `mutation {createSubscription(stripeToken: "${stripeToken}") {errors}}`,
    },
    token
  )
  return { errors }
}

export async function cancelSubscription(token) {
  const {
    data: {
      cancelSubscription: { errors },
    },
  } = await fetchGQL(
    {
      query: `mutation {cancelSubscription {errors}}`,
    },
    token
  )
  return { errors }
}

export async function flagProperty(data) {
  const {
    data: {
      flagProperty: { errors },
    },
  } = await fetchGQL(
    {
      query: `mutation { flagProperty(id: "${data.id}", reason: "${data.reason}") { errors } }`,
    },
    data.token
  )
  return { errors }
}

export async function contactUs(params) {
  const {
    data: {
      contactUs: { errors },
    },
  } = await fetchGQL({
    query: `mutation { contactUs(name: "${params.name}", email: "${params.email}", phone: "${params.phone}", message: "${params.message}") {errors} }`,
  })

  return { errors }
}



// WEBPACK FOOTER //
// ./src/services/api/backend.js